import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Star, Loader2, CheckCircle2 } from "lucide-react";
import { BRAND } from "../lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReviewPage() {
  const { id } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    axios
      .get(`${API}/appointments/${id}/review-info`)
      .then(({ data }) => {
        setInfo(data);
        if (data.already_reviewed) setDone(true);
      })
      .catch(() => setError("We couldn't find this review link."))
      .finally(() => setLoading(false));
  }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    if (rating < 1) {
      setError("Please select a star rating.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await axios.post(`${API}/appointments/${id}/review`, { rating, comment });
      setDone(true);
    } catch (err) {
      if (err.response?.status === 409) {
        setDone(true); // already reviewed — show thank-you
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-sand flex items-center justify-center px-6 py-16">
      <div className="grain-overlay" />
      <div className="relative z-10 w-full max-w-lg">
        <div className="text-center mb-8">
          <img src={BRAND.logo} alt={BRAND.name} className="h-16 w-16 rounded-full object-cover mx-auto" />
          <p className="mt-4 text-xs uppercase tracking-[0.25em] text-clay">{BRAND.name}</p>
        </div>

        <div className="bg-sand-dark rounded-3xl p-8 md:p-10 border border-charcoal/5 shadow-sm">
          {loading ? (
            <div className="flex justify-center py-10 text-clay"><Loader2 className="animate-spin" /></div>
          ) : error && !info ? (
            <p className="text-center text-stone" data-testid="review-error">{error}</p>
          ) : done ? (
            <div className="text-center py-4" data-testid="review-success">
              <CheckCircle2 className="text-clay mx-auto" size={48} strokeWidth={1.5} />
              <h1 className="mt-5 font-heading text-3xl text-charcoal font-light">Thank you!</h1>
              <p className="mt-3 text-stone font-light">
                Your feedback means the world to {info?.business_name || BRAND.name}.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="review-form">
              <h1 className="font-heading text-3xl text-charcoal font-light text-center">
                How was your experience?
              </h1>
              <p className="mt-3 text-stone font-light text-center">
                {info?.horse_name
                  ? `We'd love to hear how things went with ${info.horse_name}.`
                  : "We'd love to hear your feedback."}
              </p>

              <div className="flex justify-center gap-2 my-8" data-testid="review-stars">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    data-testid={`star-${n}`}
                    onMouseEnter={() => setHover(n)}
                    onMouseLeave={() => setHover(0)}
                    onClick={() => setRating(n)}
                    className="transition-transform hover:scale-110"
                  >
                    <Star
                      size={40}
                      strokeWidth={1.5}
                      className={(hover || rating) >= n ? "text-clay fill-clay" : "text-charcoal/20"}
                    />
                  </button>
                ))}
              </div>

              <textarea
                data-testid="review-comment"
                rows={4}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Tell us a little about your experience (optional)"
                className="w-full bg-sand border border-charcoal/15 rounded-2xl px-4 py-3 text-charcoal placeholder:text-stone/50 focus:outline-none focus:border-clay transition-colors resize-none"
              />

              {error ? <p className="text-red-700 text-sm mt-3 text-center">{error}</p> : null}

              <button
                type="submit"
                data-testid="review-submit"
                disabled={submitting}
                className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-clay text-sand rounded-full px-8 py-4 font-medium transition-all duration-300 hover:bg-clay-dark disabled:opacity-60"
              >
                {submitting ? <Loader2 className="animate-spin" size={18} /> : null}
                {submitting ? "Submitting..." : "Submit review"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
